import { attach, createEvent, type Effect, restore, sample, split, type UnitTargetable } from 'effector'
import { not } from 'patronum'

import type { PortalAPIError } from '@portal/portal-api'

import { getPortalAPIErrorMessage } from '@/shared/api/portal/error-message'
import { $i18n } from '@/shared/lib/i18n'

import { EmailConfirmDialog } from '../ui/email-confirm-dialog'

type AttachEmailConfirmationParams<Params, Done, Fail> = {
    effect: Effect<Params, Done, Fail>
    onSuccess?: UnitTargetable<{ email: string }>
} & (
    | {
          onError: UnitTargetable<{ error: PortalAPIError }>
          onErrorMessage?: never
      }
    | {
          onError?: never
          onErrorMessage: UnitTargetable<{ message: string }>
      }
)

type EmailEffectParams = {
    email: string
    skip_email_validation?: boolean
}

export function attachEmailConfirmation<Params extends EmailEffectParams, Done>(
    params: AttachEmailConfirmationParams<Params, Done, PortalAPIError>
): Effect<Params, Done, PortalAPIError> {
    const emailConfirm = createEvent()

    const attachedActionFx = attach({ effect: params.effect })

    const $lastEmailFxParams = restore(
        attachedActionFx.finally.map(({ params }) => params),
        null
    )

    if (params.onSuccess) {
        sample({
            clock: attachedActionFx.done,
            fn: (data) => ({ email: data.params.email }),
            target: params.onSuccess,
        })
    }

    const emailFxFailStatus = split(attachedActionFx.fail, {
        unknown: ({ error }) => error.code === 'email_maybe_undeliverable',
    })

    sample({
        clock: emailFxFailStatus.unknown,
        fn: ({ params }) => ({
            email: params.email,
            onConfirm: emailConfirm,
            $disableSubmitButton: attachedActionFx.pending,
        }),
        target: EmailConfirmDialog.model.showFx,
    })

    sample({
        clock: sample({ clock: emailConfirm, filter: not(attachedActionFx.pending) }),
        source: $lastEmailFxParams,
        filter: (params): params is Params => params !== null,
        fn: (params: Params): Params => ({ ...params, skip_email_validation: true }),
        target: attachedActionFx,
    })

    sample({
        clock: attachedActionFx.done,
        target: EmailConfirmDialog.model.hideFx,
    })

    const attachedActionFailed = createEvent<{ error: PortalAPIError }>()

    sample({
        clock: emailFxFailStatus.__,
        fn: ({ error }) => ({ error: error }),
        target: attachedActionFailed,
    })

    if (params.onError) {
        sample({
            clock: attachedActionFailed,
            target: params.onError,
        })
    }

    if (params.onErrorMessage) {
        sample({
            clock: attachedActionFailed,
            source: $i18n,
            fn: ({ i18n }, { error }) => ({ message: getPortalAPIErrorMessage(error, i18n) }),
            target: params.onErrorMessage,
        })
    }

    sample({
        clock: emailFxFailStatus.__,
        target: EmailConfirmDialog.model.hideFx,
    })

    return attachedActionFx
}
